import { useCallback, useEffect, useRef, useState } from "react";
import type { LaunchHubModel } from "@/lib/core/schemas/launch-hub";
import logger from "@/lib/logger";
import { fetchLaunchHub } from "@/src/api/launch-hub";

export type LaunchHubHookState =
  | { state: "loading" }
  | { state: "failed"; retry: () => void }
  | {
      state: "ready" | "refreshing";
      model: LaunchHubModel;
      refresh: () => Promise<void>;
    };

type Status = "loading" | "ready" | "failed" | "refreshing";

/**
 * Single-owner data hook for the launch hub page: one fetch owner, one
 * in-flight AbortController (a new load aborts any prior one; unmount aborts
 * the last one), and a state machine that never wipes a rendered model out
 * from under the user — `refresh()` keeps the previous model visible under
 * `state: "refreshing"` until the new one lands (or falls back to `failed`
 * if it doesn't).
 */
export function useLaunchHub(): LaunchHubHookState {
  const [model, setModel] = useState<LaunchHubModel | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  const load = useCallback((mode: "loading" | "refreshing"): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus(mode);
    return fetchLaunchHub(controller.signal)
      .then((fetched) => {
        if (controller.signal.aborted || !mountedRef.current) {
          return;
        }
        setModel(fetched);
        setStatus("ready");
      })
      .catch((err) => {
        if (controller.signal.aborted || !mountedRef.current) {
          return;
        }
        logger.error({ err }, "[LaunchHub] fetch failed");
        setStatus("failed");
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load("loading");
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, [load]);

  const retry = useCallback(() => {
    load("loading");
  }, [load]);

  const refresh = useCallback(() => load("refreshing"), [load]);

  if (status === "loading") {
    return { state: "loading" };
  }
  if (status === "failed" || !model) {
    return { state: "failed", retry };
  }
  return { state: status, model, refresh };
}
